import type { TerrainOnlyTileKind, Tile } from "@dailywars/core/engine";
import { DEFAULT_MAP_THEME, mapThemeConfig, type MapTheme } from "@dailywars/core/render/mapThemes";

import { drawBuildingImage } from "#/game/buildingDraw";
import { isImageReady } from "#/game/imageReady";
import { resolveSeaShoreImage, resolveSeaShoreOverlayImages } from "#/game/seaShoreSprites";
import { getTerrainImage } from "#/game/svgAssets";
import {
  getSpriteTerrainImage,
  usesSpriteTerrainFullTile,
  usesSpriteTerrainOverlay,
} from "#/game/terrainSprites";

function drawFilledTile({
  ctx,
  px,
  py,
  tileSize,
  fillStyle,
}: {
  ctx: CanvasRenderingContext2D;
  px: number;
  py: number;
  tileSize: number;
  fillStyle: string;
}): void {
  ctx.fillStyle = fillStyle;
  ctx.fillRect(px, py, tileSize, tileSize);
}

function drawSeaShoreOverlays({
  ctx,
  px,
  py,
  tileSize,
  tiles,
  width,
  x,
  y,
  theme,
}: {
  ctx: CanvasRenderingContext2D;
  px: number;
  py: number;
  tileSize: number;
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
  theme: MapTheme;
}): void {
  for (const overlayImg of resolveSeaShoreOverlayImages({ tiles, width, x, y, theme })) {
    if (!isImageReady(overlayImg)) continue;
    ctx.drawImage(overlayImg, px, py, tileSize, tileSize);
  }
}

function drawSeaBaseTile({
  ctx,
  px,
  py,
  theme,
  tileSize,
  tiles,
  width,
  x,
  y,
}: {
  ctx: CanvasRenderingContext2D;
  px: number;
  py: number;
  theme: MapTheme;
  tileSize: number;
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
}): void {
  const terrainImg = resolveSeaShoreImage({ tiles, width, x, y, theme });
  if (isImageReady(terrainImg)) {
    ctx.drawImage(terrainImg, px, py, tileSize, tileSize);
    return;
  }
  drawFilledTile({
    ctx,
    px,
    py,
    tileSize,
    fillStyle: mapThemeConfig(theme).seaColors[0]!,
  });
}

function drawFullSpriteTerrainTile({
  ctx,
  kind,
  px,
  py,
  theme,
  tileSize,
  tiles,
  width,
  x,
  y,
}: {
  ctx: CanvasRenderingContext2D;
  kind: TerrainOnlyTileKind;
  px: number;
  py: number;
  theme: MapTheme;
  tileSize: number;
  tiles?: readonly Tile[];
  width?: number;
  x?: number;
  y?: number;
}): void {
  if (
    kind === "sea" &&
    tiles !== undefined &&
    width !== undefined &&
    x !== undefined &&
    y !== undefined
  ) {
    drawSeaBaseTile({ ctx, px, py, theme, tileSize, tiles, width, x, y });
    drawSeaShoreOverlays({ ctx, px, py, tileSize, tiles, width, x, y, theme });
    return;
  }

  const terrainImg = getSpriteTerrainImage({ theme, kind });
  if (isImageReady(terrainImg)) {
    ctx.drawImage(terrainImg, px, py, tileSize, tileSize);
    return;
  }
  drawFilledTile({
    ctx,
    px,
    py,
    tileSize,
    fillStyle: mapThemeConfig(theme).seaColors[0]!,
  });
}

function drawOverlaySpriteTerrainTile({
  ctx,
  kind,
  x,
  y,
  px,
  py,
  theme,
  tileSize,
}: {
  ctx: CanvasRenderingContext2D;
  kind: TerrainOnlyTileKind;
  x: number;
  y: number;
  px: number;
  py: number;
  theme: MapTheme;
  tileSize: number;
}): void {
  const plainImg = getTerrainImage({ kind: "plain", x, y, theme });
  if (isImageReady(plainImg)) {
    ctx.drawImage(plainImg, px, py, tileSize, tileSize);
  } else {
    drawFilledTile({
      ctx,
      px,
      py,
      tileSize,
      fillStyle: mapThemeConfig(theme).plainColors[0],
    });
  }
  const overlayImg = getSpriteTerrainImage({ theme, kind });
  if (isImageReady(overlayImg)) {
    drawBuildingImage({ ctx, img: overlayImg, tileX: x, tileY: y, tileSize });
  }
}

function drawSvgTerrainTile({
  ctx,
  kind,
  x,
  y,
  px,
  py,
  theme,
  tileSize,
}: {
  ctx: CanvasRenderingContext2D;
  kind: TerrainOnlyTileKind;
  x: number;
  y: number;
  px: number;
  py: number;
  theme: MapTheme;
  tileSize: number;
}): void {
  const terrainImg = getTerrainImage({ kind, x, y, theme });
  if (isImageReady(terrainImg)) {
    ctx.drawImage(terrainImg, px, py, tileSize, tileSize);
    return;
  }
  drawFilledTile({
    ctx,
    px,
    py,
    tileSize,
    fillStyle: mapThemeConfig(theme).plainColors[0],
  });
}

export function drawTerrainTile({
  ctx,
  kind,
  x,
  y,
  theme,
  tileSize,
  tiles,
  width,
}: {
  ctx: CanvasRenderingContext2D;
  kind: TerrainOnlyTileKind;
  x: number;
  y: number;
  theme?: MapTheme;
  tileSize: number;
  tiles?: readonly Tile[];
  width?: number;
}): void {
  const selectedTheme = theme ?? DEFAULT_MAP_THEME;
  const px = x * tileSize;
  const py = y * tileSize;

  if (usesSpriteTerrainFullTile({ kind })) {
    drawFullSpriteTerrainTile({
      ctx,
      kind,
      px,
      py,
      theme: selectedTheme,
      tileSize,
      tiles,
      width,
      x,
      y,
    });
    return;
  }

  if (usesSpriteTerrainOverlay({ theme: selectedTheme, kind })) {
    drawOverlaySpriteTerrainTile({ ctx, kind, x, y, px, py, theme: selectedTheme, tileSize });
    return;
  }

  drawSvgTerrainTile({ ctx, kind, x, y, px, py, theme: selectedTheme, tileSize });
}
